import { supabase } from './supabase'

// Phase 0.3 — client API for custom storefront domains. READS go through the member
// RPC get_custom_domains (mig 328); WRITES (add/status/remove) go through the
// custom-domain-manager edge function (§3), which owner-verifies + talks to Vercel.
// Domains live in their OWN table (tenant_custom_domains), never the tenant blob — so
// nothing here touches tenants / setTenant.

export type CustomDomainStatus = 'pending_dns' | 'verifying' | 'active' | 'failed' | 'removed'

export type DnsRecord = { type: string; name: string; value: string; note?: string }

export type DomainVerification = {
  records?: DnsRecord[]
  checked_at?: string
  // Live Vercel health stored by the edge function (may be absent on rows written before
  // the health fields shipped — treated as "unknown", never as a false alarm).
  misconfigured?: boolean
  change_recommended?: boolean
}

export type CustomDomain = {
  id: string
  domain: string
  status: CustomDomainStatus
  verification: DomainVerification | null
  is_primary: boolean
  created_at: string
  verified_at: string | null
  last_checked_at: string | null
}

// Distinct health for the UI, derived from BOTH the lifecycle status and the live Vercel
// flags. A domain can be lifecycle-'active' (still resolving, shop reachable) yet need a DNS
// change — that is 'action_needed', NOT 'live'. Green "Live" is only for a clean active
// domain. This is the signal that would have warned Nature's Nurture before it went dark.
export type DomainHealth = 'live' | 'action_needed' | 'verifying' | 'pending'

export function domainHealth(d: Pick<CustomDomain, 'status' | 'verification'>): DomainHealth {
  const v = d.verification
  const needsDns = v?.misconfigured === true || v?.change_recommended === true
  if (d.status === 'active') return needsDns ? 'action_needed' : 'live'
  if (d.status === 'failed') return 'action_needed'
  if (d.status === 'verifying') return 'verifying'
  return 'pending'
}

export type ManagerAction = 'add' | 'status' | 'remove'

export type ManagerResult =
  | { ok: true; action: ManagerAction; domain: CustomDomain | string }
  | { ok: false; reason: string; detail?: string }

// Human-readable copy for the reasons the edge function / RPC can return.
export function customDomainErrorMessage(reason: string | undefined, detail?: string): string {
  switch (reason) {
    case 'domain_taken':
      return 'That domain is already connected to another Palstro store.'
    case 'domain_in_use_elsewhere':
      return 'That domain is already in use on another Vercel project. Remove it there first, then try again.'
    case 'invalid_domain':
    case 'missing_domain':
      return 'Enter a valid domain, e.g. your-shop.com (no “http://”, no path).'
    case 'owner_only':
      return 'Only the store owner can connect or remove a domain.'
    case 'vercel_not_configured':
      return 'Custom domains aren’t switched on for this platform yet. Ask your administrator to add the Vercel credentials.'
    case 'domain_not_found':
      return 'That domain isn’t connected to this store.'
    case 'not_authenticated':
      return 'Your session has expired — please sign in again.'
    default:
      return detail ? `Something went wrong: ${detail}` : 'Something went wrong. Please try again.'
  }
}

export async function fetchCustomDomains(tenantId: string): Promise<CustomDomain[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('get_custom_domains', {
    p_tenant_id: tenantId,
  })
  if (error) throw error
  return (data ?? []) as CustomDomain[]
}

async function callManager(
  action: ManagerAction,
  tenantId: string,
  domain: string,
): Promise<ManagerResult> {
  const { data, error } = await supabase.functions.invoke('custom-domain-manager', {
    body: { action, tenant_id: tenantId, domain },
  })
  if (error) {
    // supabase-js surfaces a non-2xx as a FunctionsHttpError whose .context is the
    // Response — read our { reason, detail } body out of it for a precise message.
    let reason = 'request_failed'
    let detail: string | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ctx = (error as any).context
    if (ctx && typeof ctx.json === 'function') {
      try {
        const body = await ctx.json()
        reason = body?.reason ?? reason
        detail = body?.detail
      } catch {
        /* non-JSON body — keep the generic reason */
      }
    }
    return { ok: false, reason, detail }
  }
  return data as ManagerResult
}

export const addCustomDomain = (tenantId: string, domain: string) =>
  callManager('add', tenantId, domain)

export const refreshCustomDomainStatus = (tenantId: string, domain: string) =>
  callManager('status', tenantId, domain)

export const removeCustomDomain = (tenantId: string, domain: string) =>
  callManager('remove', tenantId, domain)

// Mirror of the server-side is_valid_custom_domain (mig 327) for instant client-side
// feedback before we bother the edge function. The server is still the authority.
export function looksLikeValidDomain(raw: string): boolean {
  const v = (raw || '')
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/[/:?].*$/, '')
    .replace(/\.$/, '')
  if (!v || v.length > 253) return false
  if (v.includes(':')) return false
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(v)) return false
  if (v === 'palstro.com' || v.endsWith('.palstro.com')) return false
  if (v === 'localhost' || v.endsWith('.localhost')) return false
  if (!/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?))+$/.test(v)) return false
  return /\.[a-z]{2,}$/.test(v)
}

export function normalizeDomainInput(raw: string): string {
  return (raw || '')
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/[/:?].*$/, '')
    .replace(/\.$/, '')
}
